import React from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory, {
  PaginationProvider,
  SizePerPageDropdownStandalone,
  PaginationListStandalone,
} from 'react-bootstrap-table2-paginator'
import filterFactory, { textFilter, selectFilter } from 'react-bootstrap-table2-filter'
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min'
import { CSVExport } from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min'
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css'
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css'
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css'
import './Backup365ReconsTable.css'

import { useDispatch, useSelector } from 'react-redux'
import {
  refreshBackupRecon,
  handleSyncCompany,
  handleAutoRecon,
  handleRetryFailedSyncs,
  RETRY_FAILED_SYNCS_LAST_CLICK_KEY,
  RETRY_FAILED_SYNCS_COOLDOWN_MS,
} from '../actions/backup365ReportsActions'
import { Container, Row, Col } from 'reactstrap'
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'

const { SearchBar } = Search
const { ExportCSVButton } = CSVExport

const Backup365ReconsTable = ({ companies }) => {
  const dispatch = useDispatch()
  const loadingIndex = useSelector((state) => state.backup365Reports.refreshReconloadingIndex)
  const [filteredData, setFilteredData] = React.useState(companies)
  const [syncCooldown, setSyncCooldown] = React.useState(false)
  const [autoReconRunning, setAutoReconRunning] = React.useState(false)
  const [autoReconOpen, setAutoReconOpen] = React.useState(false)
  const [retryFailedRunning, setRetryFailedRunning] = React.useState(false)

  // 60-minute cooldown persisted in localStorage so it survives page refreshes / tab
  // changes. Backed by a matching server-side cooldown on POST /RetryFailedSyncs that
  // returns 429 if a duplicate batch arrives within the window — the localStorage timer
  // is the polite UX hint, the server is the correctness gate.
  const readLastClick = () => {
    try {
      const raw = window.localStorage.getItem(RETRY_FAILED_SYNCS_LAST_CLICK_KEY)
      const parsed = raw ? parseInt(raw, 10) : 0
      return Number.isFinite(parsed) ? parsed : 0
    } catch {
      return 0
    }
  }

  const [now, setNow] = React.useState(() => Date.now())
  React.useEffect(() => {
    // Re-render every 30s so the "Available in Xm" countdown updates without
    // requiring user interaction. Cheap; component is admin-only.
    const id = setInterval(() => setNow(Date.now()), 30 * 1000)
    return () => clearInterval(id)
  }, [])

  const lastClick = readLastClick()
  const msSinceClick = now - lastClick
  const onCooldown = lastClick > 0 && msSinceClick < RETRY_FAILED_SYNCS_COOLDOWN_MS
  const minutesLeft = onCooldown
    ? Math.max(1, Math.ceil((RETRY_FAILED_SYNCS_COOLDOWN_MS - msSinceClick) / 60000))
    : 0

  const failedCount = (companies || []).filter((c) => c && c.syncSuccess === false).length

  const retryButtonDisabled =
    retryFailedRunning || failedCount === 0 || onCooldown

  const retryButtonTitle = (() => {
    if (failedCount === 0) return 'No failed syncs to retry'
    if (onCooldown) return `Available again in ${minutesLeft} minute(s)`
    return `Re-queue sync for ${failedCount} ${failedCount === 1 ? 'company' : 'companies'} with failed status`
  })()

  const handleRetryFailedClick = () => {
    if (retryButtonDisabled) return
    setRetryFailedRunning(true)
    try {
      dispatch(handleRetryFailedSyncs())
    } finally {
      // UX delay only — jobs continue server-side. Action stamps localStorage on
      // success; setting state here just re-enables the button after a brief gap so
      // the spinner has time to render.
      setTimeout(() => {
        setRetryFailedRunning(false)
        setNow(Date.now())
      }, 2000)
    }
  }

  const toggleAutoRecon = () => {
    if (!autoReconRunning) {
      setAutoReconOpen((prev) => !prev)
    }
  }

  const handleAutoReconOption = (reportOnly) => {
    if (autoReconRunning) return

    setAutoReconRunning(true)

    try {
      dispatch(handleAutoRecon(reportOnly))
    } finally {
      // UX delay only – job continues server-side
      setTimeout(() => setAutoReconRunning(false), 2000)
    }
  }

  const columns = [
    {
      dataField: 'companyName',
      text: 'Company Name',
      sort: true,
      filter: textFilter({ placeholder: 'Filter by name...' }),
    },
    {
      dataField: 'companyType',
      text: 'Type',
      sort: true,
      filter: selectFilter({
        options: { Managed: 'Managed', Essentials: 'Essentials' },
        placeholder: 'Filter by type...',
      }),
    },
    {
      dataField: 'syncSuccess',
      text: 'Sync Status',
      sort: true,
      formatter: (cell) => (
        <div className="d-flex align-items-center gap-1">
          <span
            style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              marginRight: '2px',
              backgroundColor: cell ? '#28a745' : '#dc3545',
            }}
          ></span>
          <span>{cell ? ' Success' : ' Failed'}</span>
        </div>
      ),
      filter: selectFilter({
        options: { true: 'Success', false: 'Failed' },
        placeholder: 'Filter...',
      }),
    },
    {
      dataField: 'syncErrors',
      text: 'Sync Errors',
      formatter: (cell) => (cell && cell.length > 60 ? `${cell.substring(0, 60)}...` : cell || '-'),
    },
    {
      dataField: 'latestReportSyncDate',
      text: 'Last Sync',
      sort: true,
      formatter: (cell) =>
        cell ? new Date(cell).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }) : '-',
    },
    {
      dataField: 'reconciliationMonth',
      text: 'Reconciliation Month',
      sort: true,
      formatter: (cell) => new Date(cell).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }),
    },
    {
      dataField: 'unchangedSinceLastReconciliation',
      text: 'Change Status',
      filter: selectFilter({
        options: { true: 'No Changes', false: 'Changes Detected' },
        placeholder: 'Filter...',
      }),
      formatter: (cell) => (
        <div className="d-flex align-items-center gap-1">
          <span
            style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              marginRight: '2px',
              backgroundColor: cell ? '#6c757d' : '#0d6efd',
            }}
          ></span>
          <span>{cell ? ' No Changes' : ' Changes Detected'}</span>
        </div>
      ),
    },
    {
      dataField: 'reconciled',
      text: 'Reconciled',
      sort: true,
      filter: selectFilter({
        options: { true: 'Yes', false: 'No' },
        placeholder: 'Filter...',
      }),
      formatter: (cell) => (cell ? 'Yes' : 'No'),
    },

    {
      dataField: 'actions',
      text: 'Actions',
      isDummyField: true,
      csvExport: false,
      style: {
        width: '140px',
        minWidth: '140px',
        display: 'flex',
        gap: '4px',
      },
      formatter: (cell, row, rowIndex) => {
        const isLoading = loadingIndex === rowIndex
        return (
          <Row>
            <Col md={6}>
              <button
                className="btn btn-sm btn-outline-primary d-flex align-items-center me-4"
                disabled={isLoading}
                onClick={() => dispatch(refreshBackupRecon(row.companyId))}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    Refreshing…
                  </>
                ) : (
                  'Refresh'
                )}
              </button>
            </Col>
            <Col md={6}>
              <button
                className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                disabled={syncCooldown}
                onClick={() => {
                  // Start cooldown immediately
                  setSyncCooldown(true)
                  setTimeout(() => setSyncCooldown(false), 2000)

                  // Fire actual sync action
                  dispatch(handleSyncCompany(row.companyId))
                }}
              >
                {syncCooldown ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    syncing....
                  </>
                ) : (
                  'Sync'
                )}
              </button>
            </Col>
          </Row>
        )
      },
    },
  ]

  const defaultSorted = [{ dataField: 'companyName', order: 'asc' }]

  return (
    <PaginationProvider
      pagination={paginationFactory({
        custom: true,
        totalSize: filteredData.length,
        sizePerPageList: [
          { text: '10', value: 10 },
          { text: '20', value: 20 },
          { text: '50', value: 50 },
          { text: '100', value: 100 },
          { text: 'All', value: filteredData.length },
        ],
      })}
    >
      {({ paginationProps, paginationTableProps }) => (
        <ToolkitProvider
          keyField="companyId"
          data={companies || []}
          columns={columns}
          search
          exportCSV={{
            fileName: 'Backup365Companies.csv',
            onlyExportFiltered: true,
            exportAll: true,
          }}
          onDataChange={(newData) => setFilteredData(newData)}
        >
          {(props) => (
            <div className="backup365-company-table-container">
              <div className="table-toolbar">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <SearchBar {...props.searchProps} placeholder="Search companies..." />
                  <div className="d-flex gap-2 align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-danger d-flex align-items-center gap-1"
                      disabled={retryButtonDisabled}
                      title={retryButtonTitle}
                      onClick={handleRetryFailedClick}
                    >
                      {retryFailedRunning ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" />
                          Queuing…
                        </>
                      ) : onCooldown ? (
                        <>Retry Failed Syncs (available in {minutesLeft}m)</>
                      ) : (
                        <>Retry Failed Syncs ({failedCount})</>
                      )}
                    </button>

                    <Dropdown isOpen={autoReconOpen} toggle={toggleAutoRecon} direction="down">
                      <DropdownToggle
                        caret
                        color="primary"
                        disabled={autoReconRunning}
                        className="d-flex align-items-center gap-1"
                      >
                        {autoReconRunning ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" />
                            Starting…
                          </>
                        ) : (
                          'Auto Recon'
                        )}
                      </DropdownToggle>

                      <DropdownMenu end>
                        <DropdownItem onClick={() => handleAutoReconOption(false)}>Run Auto Recon</DropdownItem>

                        <DropdownItem onClick={() => handleAutoReconOption(true)}>Report Only</DropdownItem>
                      </DropdownMenu>
                    </Dropdown>

                    <ExportCSVButton className="btn btn-outline-primary" {...props.csvProps}>
                      Export CSV
                    </ExportCSVButton>

                    <SizePerPageDropdownStandalone
                      btnContextual="btn-outline-primary"
                      className="dropDownStyle"
                      {...paginationProps}
                    />
                  </div>
                </div>
              </div>

              <BootstrapTable
                bordered={false}
                hover
                condensed
                defaultSorted={defaultSorted}
                {...props.baseProps}
                pagination={paginationFactory()}
                filter={filterFactory()}
                filterPosition="top"
                {...paginationTableProps}
              />

              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted small">
                  {(() => {
                    const { page, sizePerPage } = paginationProps
                    const total = filteredData.length
                    const start = (page - 1) * sizePerPage + 1
                    const end = Math.min(page * sizePerPage, total)
                    return `Showing ${start}–${end} of ${total}`
                  })()}
                </div>

                <PaginationListStandalone {...paginationProps} />
              </div>
            </div>
          )}
        </ToolkitProvider>
      )}
    </PaginationProvider>
  )
}

export default Backup365ReconsTable
